import JoinTermsPageView from 'src/pages/JoinPage/_components/JoinTermsView'
import type { JoinTermsPageProps } from 'src/pages/JoinPage/_components/JoinTermsView'
import { termsListQueryOption, useCustomQuery } from 'src/hooks/react-query/useCustomQuery.hook'
import {
  JoinWithOtpCertSubmitSpace,
  PushTokenUpdateSpace,
  ResponseDataType,
  TelecomType,
  TermsListSpace,
  VulerableCheckSpace,
} from 'src/types/api'
import React, { useEffect } from 'react'
import useJoinTermsAgreeForm from 'src/hooks/react-hook-form/useJoinTermsAgreeForm.hook'
import { FormProvider, SubmitHandler, useFormContext } from 'react-hook-form'
import { JoinOtpCertFormItemsType, JoinTermsAgreeItemsType } from 'src/types/react-hook-form'
import {
  joinWithOtpCertSubmitQueryOption,
  pushTokenUpdateQueryOption,
  useCustomMutation,
  vulerableCheckQueryOption,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { useCookies } from 'react-cookie'
import { ERRCODE_SUCCESS, ERRCODES_OTPCERTSUBMIT } from 'src/service/constant/errcodes'
import { useModal } from 'src/provider/ModalContextProvider'
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form'
import { DateUtils } from 'src/utils/date'
import usePageNav from 'src/hooks/usePageNav.hook'
import { Loading } from 'src/components/common/Loading'
import RenderIf from 'src/components/common/RenderIf'
import { complete_signup_dataLayer } from 'src/utils/gtmDataLayer'
import { animateScroll } from 'react-scroll'
import { Bridge } from 'src/utils/bridge'
import TermsModal from 'src/components/common/Modal/children/TermsModal'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import VulnerableModal from 'src/components/common/Modal/children/VulnerableModal'
import usePartnerEntry from 'src/hooks/usePartnerEntry.hook'
import { encryptPhoneNumber } from 'src/utils'
import { useToast } from 'src/provider/ToastContextProvider'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'

type JoinTermsSubmitHandler = () => void | Promise<void>

export interface JoinTermsFlowProps {
  countStop: () => void
  onJoinTermsSubmitReady?: (submit: JoinTermsSubmitHandler | null) => void
}

const JoinTermsFlow = ({ countStop, onJoinTermsSubmitReady }: JoinTermsFlowProps) => {
  const { getValues } = useFormContext<JoinOtpCertFormItemsType>()

  const { data: termsListData, isLoading } = useCustomQuery<TermsListSpace.Response>(
    termsListQueryOption({ Telecom: Number(getValues('agency')) as TelecomType }),
  )

  if (isLoading || !termsListData?.Body) {
    return <Loading.Default />
  }

  return (
    <JoinTermsFlowLoaded
      termsListData={termsListData}
      countStop={countStop}
      onJoinTermsSubmitReady={onJoinTermsSubmitReady}
    />
  )
}

type JoinTermsFlowLoadedProps = {
  termsListData: ResponseDataType<TermsListSpace.Response>
  countStop: () => void
  onJoinTermsSubmitReady?: (submit: JoinTermsSubmitHandler | null) => void
}

const JoinTermsFlowLoaded = ({
  termsListData,
  countStop,
  onJoinTermsSubmitReady,
}: JoinTermsFlowLoadedProps) => {
  const [, setCookie, removeCookie] = useCookies(['trans-id', 'auth-key', 'u-tel'])
  const { mainPageNav } = usePageNav()
  const { openModal } = useModal()
  const { openToast } = useToast()
  const { web } = useChannel()
  const { sessionCookieOptions } = DateUtils
  const { c_tel, c_transId } = useCustomCookies()
  const {
    value: { pCode },
  } = usePartnerEntry()

  const { resetField, setFocus, getValues, watch } = useFormContext<JoinOtpCertFormItemsType>()

  const telecom = Number(getValues('agency')) as TelecomType
  const pNumber = getValues('pNumber')?.replace(/\D/g, '') ?? ''
  const certNumber = watch('certNumber') ?? ''

  const { methods, registerAttribute, agreeAllRequiredTerms } = useJoinTermsAgreeForm(
    termsListData.Body,
    { telecom: String(telecom) },
  )

  const { mutateAsync: vulerableCheckMutate, isPending: isVulerableCheckPending } =
    useCustomMutation<VulerableCheckSpace.Request, VulerableCheckSpace.Response>(
      vulerableCheckQueryOption(),
    )

  const { mutate: pushTokenUpdateMutate } = useCustomMutation<
    PushTokenUpdateSpace.Request,
    PushTokenUpdateSpace.Response
  >(pushTokenUpdateQueryOption())

  const { mutateAsync: joinSubmitMutate, isPending: joinSubmitPending } = useCustomMutation<
    JoinWithOtpCertSubmitSpace.Request,
    JoinWithOtpCertSubmitSpace.Response
  >(joinWithOtpCertSubmitQueryOption())

  const joinTermsOnSubmit: SubmitHandler<JoinTermsAgreeItemsType> = async (data) => {
    const {
      SKTPrivacy,
      KTPrivacy,
      LGUPPrivacy,
      PrivacyThirdParty,
      Marketing,
      Insurance,
      PrivacyConsent,
      Service,
    } = data

    const agreeDetailData = {
      TermsIdx: termsListData.Body.Terms.Idx as number,
      ServiceAgree: Number(Service),
      PrivacyConsentAgree: Number(PrivacyConsent),
      PrivacyThirdPartyAgree: Number(PrivacyThirdParty),
      InsuranceAgree: Number(Insurance),
      MarketingAgree: Number(Marketing),
      SKTPrivacyAgree: telecom === 0 ? Number(SKTPrivacy) : 0,
      KTPrivacyAgree: telecom === 1 ? Number(KTPrivacy) : 0,
      LGUPPrivacyAgree: telecom === 2 ? Number(LGUPPrivacy) : 0,
    }

    const submitData = {
      TransID: c_transId,
      AgreeDetail: agreeDetailData,
      ApprovalNumber: certNumber,
      ...(!!pCode ? { PCode: pCode } : {}),
    }

    if (!c_transId) {
      openToast({ contents: '인증번호를 재전송해주세요.' })
      return
    }

    if (certNumber?.length < 4) {
      openToast({ contents: '인증번호를 입력해주세요.' })
      return
    }

    const isKeepGoing = await vulerableCheckHandler()
    if (!isKeepGoing) {
      return
    }

    addFlowBreadcrumb('join_submit', 'request', { handler: 'joinSubmit' })

    joinSubmitMutate(submitData).then(
      (res: ResponseDataType<JoinWithOtpCertSubmitSpace.Response>) => {
        const { Body, Header } = res
        const { AuthKey, PNumber } = Body

        //키보드 내리기
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }

        const handleJoinFail = (errCode: number, fn?: () => void) => {
          resetField('certNumber')
          openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '확인', f: fn } } })
          addFlowBreadcrumb('join_fail', 'response', {
            handler: 'joinSubmit',
            errCode: Header.ErrCode,
          })
        }

        switch (Header.ErrCode) {
          case ERRCODE_SUCCESS:
            setCookie('auth-key', AuthKey, sessionCookieOptions())
            setCookie('u-tel', encryptPhoneNumber(PNumber!), sessionCookieOptions())
            removeCookie('trans-id', { path: '/' })
            Bridge.handleReqSaveAuthKey(AuthKey)
            Bridge.getPushToken.req()
            mainPageNav()
            complete_signup_dataLayer()
            animateScroll.scrollToTop({ duration: 0, smooth: false })
            setTimeout(() => {
              openModal({
                contents: {
                  t: '가입완료',
                  d: '가입이 완료되었습니다.\n 진행중인 화면으로 이동합니다.',
                },
                button: {
                  submit: {
                    t: '확인',
                    f: () => {
                      countStop()
                      Bridge.handleReqSignUpLogin(PNumber, 'N')
                    },
                  },
                },
              })
            }, 1000)
            break
          case ERRCODES_OTPCERTSUBMIT.ERROR_WRONG_APPROVAL_NUMBER:
            handleJoinFail(Header.ErrCode, () => !web && setFocus('certNumber'))
            break
          case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRED_TIME:
          default:
            handleJoinFail(Header.ErrCode)
            break
          case ERRCODES_OTPCERTSUBMIT.ERROR_EXPIRATION_APPROVAL_NUMBER:
            countStop()
            removeCookie('trans-id', { path: '/' })
            handleJoinFail(Header.ErrCode)
            break
        }
      },
    )
  }

  const handleJoinTermsErrorModalConfirm = async () => {
    agreeAllRequiredTerms()
    const valid = await methods.trigger()
    if (valid) {
      await joinTermsOnSubmit(methods.getValues())
    }
  }

  const joinTermsOnError: SubmitErrorHandler<JoinTermsAgreeItemsType> = () => {
    openModal({
      contents: {
        t: `${telecom === 0 ? '휴대폰쿠폰지갑 서비스 이용/인증을 위해 약관 동의가 필요합니다.' : '서비스 이용을 위해 약관 동의가 필요합니다.'}`,
        d: '필수 약관에 모두 동의하시겠습니까?',
      },
      button: {
        direction: 'row',
        cancel: { t: '닫기' },
        submit: { t: '확인', f: handleJoinTermsErrorModalConfirm },
      },
    })
  }

  const joinTermsOpenModalHandler = ({ t, children }: { t: string; children: string }) => {
    openModal({
      contents: { t, children: <TermsModal children={children} /> },
      button: { submit: { t: '닫기' } },
    })
  }

  const vulerableCheckHandler: () => Promise<boolean> = async () => {
    return await vulerableCheckMutate({ Telecom: telecom, PNumber: pNumber })
      .then(async (res) => {
        const { Vulnerable, Message, AgePopUpYN } = res.Body
        if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
          return await new Promise<boolean>((resolve) => {
            openModal({
              type: 'custom',
              contents: {
                children: (
                  <VulnerableModal
                    onSubmitInternal={() => resolve(true)}
                    onCancelInternal={() => resolve(false)}
                    telecom={telecom}
                  />
                ),
              },
            })
          })
        } else if (Vulnerable === 'X') {
          openModal({
            contents: {
              d: Message,
            },
            button: { submit: { t: '확인' } },
          })
          return false
        } else return true
      })
      .catch((error) => {
        console.warn(error)
        return false
      })
  }

  useEffect(() => {
    Bridge.getPushToken.res((pushToken: string) => {
      pushTokenUpdateMutate({ PushToken: pushToken })
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 인증번호input에서 joinTermsSubmit 할지 otpCertSubmit 할지 결정하는 함수(part2)
  useEffect(() => {
    const handleJoinTermsAgreeSubmit = methods.handleSubmit(joinTermsOnSubmit, joinTermsOnError)
    onJoinTermsSubmitReady?.(handleJoinTermsAgreeSubmit)
    return () => onJoinTermsSubmitReady?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinTermsOnSubmit, joinTermsOnError, onJoinTermsSubmitReady])

  const props: JoinTermsPageProps = {
    registerAttribute,
    joinSubmitPending,
    joinTermsOnSubmit,
    joinTermsOnError,
    joinTermsOpenModalHandler,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={joinSubmitPending || isVulerableCheckPending}>
        <Loading.Default />
      </RenderIf>
      <JoinTermsPageView {...props} />
    </FormProvider>
  )
}

export default JoinTermsFlow
